import { useCallback, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getWithdrawals,
  subscribeToResizeWithdrawals,
} from "../services/withdrawalService";
import { WithdrawalFilters, WithdrawalItem } from "../types/withdrawal";
import { debounce } from "../utils/debounce";

function parseFiltersFromParams(params: URLSearchParams): WithdrawalFilters {
  return {
    withdrawalTimeFrom: params.get("dateFrom") || undefined,
    withdrawalTimeTo: params.get("dateTo") || undefined,
  };
}

export const useWithdrawals = () => {
  const [items, setItems] = useState<WithdrawalItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, Number(searchParams.get("page")) - 1 || 0);
  const filters = useMemo(
    () => parseFiltersFromParams(searchParams),
    [searchParams]
  );

  const fetchData = useCallback(
    async (pageToLoad: number, currentFilters: WithdrawalFilters) => {
      try {
        setLoading(true);
        setErrorMessage(null);
        const response = await getWithdrawals(
          pageToLoad,
          undefined,
          currentFilters
        );
        setItems(response.content);
        setTotalPages(response.page.totalPages);
      } catch (err) {
        setErrorMessage("Failed to load data.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(page, filters);
  }, [fetchData, page, filters]);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  useEffect(() => {
    if (isIOS) return;
    let prevHeight = window.innerHeight;
    let prevWidth = window.innerWidth;
    const handleResize = debounce(() => {
      if (
        window.innerHeight !== prevHeight ||
        window.innerWidth !== prevWidth
      ) {
        prevHeight = window.innerHeight;
        prevWidth = window.innerWidth;
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("page", "1");
          return newParams;
        });
      }
    }, 1000);

    const unsubscribe = subscribeToResizeWithdrawals(handleResize);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const setPage = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", (newPage + 1).toString());
    setSearchParams(newParams);
  };

  const applyFilters = (newFilters: WithdrawalFilters) => {
    const newParams = new URLSearchParams();
    if (newFilters.withdrawalTimeFrom) {
      newParams.set("dateFrom", newFilters.withdrawalTimeFrom);
    }
    if (newFilters.withdrawalTimeTo) {
      newParams.set("dateTo", newFilters.withdrawalTimeTo);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  return {
    items,
    page,
    totalPages,
    loading,
    errorMessage,
    setPage,
    filters,
    applyFilters,
  };
};
